<template>
  <legacy-alert v-if="visible" :tone="tone" @dismiss="dismiss">
    <span v-if="badge" class="action-mgmt-banner__badge">{{ badge }}</span>
    <slot />

    <template #cta>
      <a
        class="action-mgmt-banner__cta"
        :href="ctaHref"
        target="_blank"
        rel="noopener noreferrer"
        :aria-label="ctaLabel + ': ' + ctaDestination"
      >{{ ctaLabel }} <span aria-hidden="true">&rarr;</span></a>
    </template>
  </legacy-alert>
</template>

<script>
import LegacyAlert from '@/components/Banner/LegacyAlert';
import { isActionMgmtBannerEnabled } from '@/config/features';

export default {
  name: 'ActionMgmtBanner',
  components: { LegacyAlert },
  props: {
    flag: { type: String, required: true },
    tone: { type: String, default: 'solid' },
    badge: { type: String, default: '' },
    ctaLabel: { type: String, required: true },
    ctaHref: { type: String, required: true },
    // Spoken-form destination, so the two CTAs have distinct accessible names.
    ctaDestination: { type: String, required: true },
  },
  data() {
    return {
      dismissed: this.isDismissed(),
    };
  },
  computed: {
    visible() {
      return !this.dismissed && isActionMgmtBannerEnabled(this.$store.getters.authUser, this.flag);
    },
  },
  methods: {
    // Scoped to the user: sessionStorage outlives a logout (which redirects rather
    // than closing the tab), so an unscoped key would hide a banner from the next
    // person to sign in on this tab.
    storageKey() {
      const user = this.$store.getters.authUser;
      return `dismissed:${(user && user.userId) || 'anon'}:${this.flag}`;
    },
    // sessionStorage is per-tab and cleared on browser restart, which is exactly the
    // required lifetime: survives navigation and reload, returns in a new tab.
    isDismissed() {
      try {
        return window.sessionStorage.getItem(this.storageKey()) === '1';
      } catch (e) {
        return false;
      }
    },
    dismiss() {
      this.dismissed = true;
      try {
        window.sessionStorage.setItem(this.storageKey(), '1');
      } catch (e) {
        // Private browsing can refuse writes; closing for this view is still correct.
      }
    },
  },
};
</script>

<style scoped>
/* Badge and CTA are host-supplied — LegacyAlert only positions its slots.
   Values match the design system's LegacyAlert Storybook story. */
.action-mgmt-banner__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  min-height: 1.25rem;
  margin-right: 0.375rem;
  padding: 0.25rem;
  border-radius: 9999px;
  background-color: #fff;
  color: #262626;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
}

.action-mgmt-banner__cta {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
  box-sizing: border-box;
  border: 0;
  border-radius: 0.375rem;
  background-color: #fff;
  padding: 0.375rem 0.75rem;
  color: #362E80;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.25rem;
  text-decoration: none;
}

.action-mgmt-banner__cta:hover,
.action-mgmt-banner__cta:focus {
  color: #362E80;
  text-decoration: none;
}

.action-mgmt-banner__cta:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px currentColor;
}
</style>
